import { supabase } from "../../integrations/supabase/client";
import { uploadCompanyLogo } from "./userProfileService";

export const signUp = async (
  email: string, 
  password: string, 
  name: string, 
  companyName: string, 
  logoFile?: File
) => {
  try {
    // Upload logo first if provided
    let logoUrl: string | undefined;
    if (logoFile) {
      try {
        logoUrl = await uploadCompanyLogo(logoFile, email);
      } catch (logoError) {
        // Logo upload failed, continuing without logo
      }
    }

    // Create user account with metadata
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: {
          name: name,
          company_name: companyName,
          company_logo: logoUrl || null
        }
      }
    });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    throw error;
  }
};

export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    throw error;
  }
};

export const signOut = async () => {
  try {
    // Create a cleanup function for auth state
    const cleanupAuthState = () => {
      // Remove all Supabase auth keys from localStorage
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Remove from sessionStorage if in use
      if (typeof sessionStorage !== 'undefined') {
        Object.keys(sessionStorage).forEach((key) => {
          if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
            sessionStorage.removeItem(key);
          }
        });
      }
    };

    // Clean up auth state first
    cleanupAuthState();

    // Try to sign out with Supabase, but don't fail if session is already invalid
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      // Only throw if it's not a session missing error
      if (error && !error.message.includes('Auth session missing')) {
        throw error;
      }
    } catch (signOutError: any) {
      // Log the error but don't re-throw if it's a session issue
      if (!signOutError.message?.includes('Auth session missing') && 
          !signOutError.message?.includes('Forbidden')) {
        throw signOutError;
      }
      console.warn('Sign out warning (non-critical):', signOutError.message);
    }

    // Clean up again after sign out attempt
    cleanupAuthState();

    return { error: null };
  } catch (error: any) {
    // For any other errors, we still want to clean up local state
    console.error('Sign out error:', error);
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`
    });

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error: any) {
    throw error;
  }
};

export const resendConfirmationEmail = async (email: string) => {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`
      }
    });

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error: any) {
    throw error;
  }
};
